import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowLeft, Check, ChevronLeft, ChevronRight, Edit3,
  Headphones, KeyRound, Loader2, RefreshCw, RotateCcw, Tags, X,
} from 'lucide-react';
import { API_URL } from '../utils/api';
import { MUSICAL_KEYS } from '../utils/musicalKeys';


const PAGE_SIZE = 50;
const requestJson = async (path, options = {}) => {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.detail?.message || data?.detail || 'Request failed');
    error.status = response.status;
    error.details = data?.detail || {};
    throw error;
  }
  return data;
};


const AdminMetadataReview = () => {
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gateState = searchParams.get('gate_state');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [page, setPage] = useState(1);
  const [pack, setPack] = useState('all');
  const [group, setGroup] = useState('all');
  const [reviewState, setReviewState] = useState('needs_review');
  const [bpmState, setBpmState] = useState('all');
  const [selected, setSelected] = useState(new Map());
  const [lastBatch, setLastBatch] = useState(null);
  const [keyDialog, setKeyDialog] = useState(false);
  const [keyValue, setKeyValue] = useState('');
  const [bulkEditDialog, setBulkEditDialog] = useState(false);
  const [bulkEdit, setBulkEdit] = useState({
    titleMode: 'none', title: '', titleFind: '', titleReplace: '',
    tagMode: 'none', tags: '', tagFind: '', tagReplace: '',
  });
  const [editingLoop, setEditingLoop] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', tags: '', musicalKey: '', confirmBpm: false });
  const [pendingAction, setPendingAction] = useState(null);
  const rowRefs = useRef([]);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) navigate('/admin/login');
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  const filterQuery = useCallback((withPage = true) => {
    const params = new URLSearchParams({ review_state: reviewState, bpm_state: bpmState });
    if (gateState === 'failing') params.set('gate_state', 'failing');
    if (pack !== 'all') params.set('pack', pack);
    if (group !== 'all') params.set('instrument_group', group);
    if (withPage) {
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));
    }
    return params.toString();
  }, [reviewState, bpmState, pack, group, page, gateState]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await requestJson(`/api/admin/metadata/loops?${filterQuery()}`);
      setData(result);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [filterQuery]);

  useEffect(() => {
    if (isAuthenticated && isAdmin) loadData();
  }, [isAuthenticated, isAdmin, loadData]);

  useEffect(() => {
    setPage(1);
    setSelected(new Map());
  }, [pack, group, reviewState, bpmState]);

  const rows = data?.items || [];
  const pageIds = rows.map(row => row.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const selectionRows = useMemo(
    () => Array.from(selected, ([id, revision]) => ({ id, revision })),
    [selected],
  );

  const toggleRow = (row) => {
    setSelected(current => {
      const next = new Map(current);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row.revision);
      return next;
    });
  };

  const togglePage = () => {
    setSelected(current => {
      const next = new Map(current);
      if (allPageSelected) rows.forEach(row => next.delete(row.id));
      else rows.forEach(row => next.set(row.id, row.revision));
      return next;
    });
  };

  const selectAllMatching = async () => {
    setWorking(true);
    try {
      const resolved = await requestJson(`/api/admin/metadata/selection?${filterQuery(false)}`);
      if (!resolved.selection_available) {
        toast.error(`${resolved.count} loops match. Refine filters to 500 or fewer before selecting all.`);
        return;
      }
      setSelected(new Map(resolved.loops.map(row => [row.id, row.revision])));
      toast.success(`${resolved.count} matching loops selected`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setWorking(false);
    }
  };

  const handleFailure = (error) => {
    const drifted = error.details?.drifted_ids;
    if (drifted?.length) {
      toast.error(`${drifted.length} loop revision${drifted.length === 1 ? '' : 's'} changed. The whole action was rejected.`);
      loadData();
    } else {
      toast.error(error.message);
    }
  };

  const performAction = async (action) => {
    setPendingAction(null);
    setWorking(true);
    try {
      const result = await requestJson(action.path, {
        method: action.method || 'POST',
        body: JSON.stringify(action.body),
      });
      setLastBatch({ id: result.batch_id, label: action.successLabel, count: result.updated_count });
      setSelected(new Map());
      setKeyDialog(false);
      setBulkEditDialog(false);
      setEditingLoop(null);
      toast.success(`${action.successLabel} for ${result.updated_count} loop${result.updated_count === 1 ? '' : 's'}`);
      await loadData();
    } catch (error) {
      handleFailure(error);
    } finally {
      setWorking(false);
    }
  };

  const runBulk = (action) => {
    if (!selectionRows.length) {
      toast.error('Select at least one loop');
      return;
    }
    const withSelection = { ...action, body: { ...action.body, loops: selectionRows } };
    if (selectionRows.length > 100) {
      setKeyDialog(false);
      setBulkEditDialog(false);
      setPendingAction(withSelection);
    }
    else performAction(withSelection);
  };

  const undoLastBatch = async () => {
    if (!lastBatch) return;
    setWorking(true);
    try {
      const result = await requestJson(`/api/admin/metadata/undo/${lastBatch.id}`, { method: 'POST' });
      toast.success(`Restored ${result.updated_count} loop${result.updated_count === 1 ? '' : 's'}`);
      setLastBatch(null);
      setSelected(new Map());
      await loadData();
    } catch (error) {
      handleFailure(error);
    } finally {
      setWorking(false);
    }
  };

  const submitBulkEdit = () => {
    const body = {};
    if (bulkEdit.titleMode === 'set') body.title = bulkEdit.title;
    if (bulkEdit.titleMode === 'replace') {
      body.title_find = bulkEdit.titleFind;
      body.title_replace = bulkEdit.titleReplace;
    }
    if (bulkEdit.tagMode === 'set') body.tags = bulkEdit.tags.split(',');
    if (bulkEdit.tagMode === 'replace') {
      body.tag_find = bulkEdit.tagFind;
      body.tag_replace = bulkEdit.tagReplace;
    }
    if (!Object.keys(body).length) {
      toast.error('Choose a title or tag edit');
      return;
    }
    runBulk({ path: '/api/admin/metadata/bulk/edit', body, successLabel: 'Metadata updated' });
  };

  const openRowEdit = (row) => {
    setEditingLoop(row);
    setEditForm({
      title: row.title,
      tags: row.tags.join(', '),
      musicalKey: row.musical_key || '',
      confirmBpm: false,
    });
  };

  const submitRowEdit = () => {
    const body = {
      expected_revision: editingLoop.revision,
      title: editForm.title,
      tags: editForm.tags.split(','),
    };
    if (editForm.musicalKey) body.musical_key = editForm.musicalKey;
    if (editForm.confirmBpm) body.confirm_suggested_bpm = true;
    performAction({
      path: `/api/admin/metadata/loops/${editingLoop.id}`,
      method: 'PATCH',
      body,
      successLabel: 'Loop updated',
    });
  };

  const onRowKeyDown = (event, row, index) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleRow(row);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      rowRefs.current[Math.min(index + 1, rows.length - 1)]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      rowRefs.current[Math.max(index - 1, 0)]?.focus();
    } else if (event.key.toLowerCase() === 'e') {
      event.preventDefault();
      openRowEdit(row);
    }
  };

  const onAudioPlay = (event) => {
    document.querySelectorAll('audio[data-metadata-preview]').forEach(audio => {
      if (audio !== event.currentTarget) audio.pause();
    });
  };

  const audioUrl = (url) => (url?.startsWith('/') ? `${API_URL}${url}` : url);
  const progress = data?.progress || {};
  const reviewed = Math.max(0, (progress.total_loops || 0) - (progress.needs_review || 0));
  const reviewedPercent = progress.total_loops ? Math.round((reviewed / progress.total_loops) * 100) : 0;

  if (authLoading || (!data && loading)) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-primary" /></div>;
  }

  return (
    <div data-testid="admin-metadata-review" className="min-h-screen bg-background pb-28">
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin"><Button variant="ghost" size="icon" className="rounded-full" aria-label="Back to admin"><ArrowLeft className="w-5 h-5" /></Button></Link>
            <div className="min-w-0">
              <h1 className="font-heading font-bold text-xl truncate">Catalog Metadata Review</h1>
              <p className="text-xs text-muted-foreground">Listen, verify, and confirm reconstructed loop data</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Link to="/admin/tagging"><Button variant="ghost" size="sm" className="rounded-full">Tagging Studio</Button></Link>
            <Button variant="ghost" size="sm" className="rounded-full" onClick={loadData} disabled={loading || working}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 py-5 space-y-4">
        <section className="grid md:grid-cols-[minmax(260px,1.5fr)_repeat(3,minmax(130px,0.6fr))] gap-3">
          <div className="rounded-2xl border border-white/10 bg-card/60 p-4">
            <div className="flex items-end justify-between mb-2">
              <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Catalog progress</p><p className="text-2xl font-bold">{reviewed.toLocaleString()} <span className="text-sm font-normal text-muted-foreground">of {progress.total_loops?.toLocaleString() || 0} reviewed</span></p></div>
              <span className="text-sm font-semibold text-primary">{reviewedPercent}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden bg-white/10"><div className="h-full bg-primary transition-all" style={{ width: `${reviewedPercent}%` }} /></div>
          </div>
          <Stat label="Still need review" value={progress.needs_review} tone="text-amber-400" />
          <Stat label="Suggested BPM only" value={progress.bpm_suggested} tone="text-amber-400" />
          <Stat label="Missing key" value={progress.key_missing} tone="text-violet-400" />
        </section>

        {lastBatch && (
          <section className="rounded-2xl border border-primary/40 bg-primary/10 px-4 py-3 flex items-center justify-between gap-3" role="status">
            <div className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-primary" /><span>{lastBatch.label} for {lastBatch.count} loops.</span></div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="rounded-full border-primary/50 text-primary" onClick={undoLastBatch} disabled={working}><RotateCcw className="w-4 h-4 mr-2" />Undo now</Button>
              <Button size="icon" variant="ghost" className="w-8 h-8 rounded-full" onClick={() => setLastBatch(null)} aria-label="Dismiss undo"><X className="w-4 h-4" /></Button>
            </div>
          </section>
        )}

        <section className="rounded-2xl border border-white/10 bg-card/50 p-3 space-y-3">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <FilterSelect label="Pack" value={pack} onChange={setPack} options={[{ value: 'all', label: 'All packs' }, { value: 'unassigned', label: 'Unassigned' }, ...(data?.filter_options?.packs || []).map(item => ({ value: item.id, label: item.name }))]} />
            <FilterSelect label="Instrument" value={group} onChange={setGroup} options={[{ value: 'all', label: 'All instruments' }, ...(data?.filter_options?.instrument_groups || []).map(item => ({ value: item, label: item }))]} />
            <FilterSelect label="Review state" value={reviewState} onChange={setReviewState} options={[{ value: 'needs_review', label: 'Needs review' }, { value: 'reviewed', label: 'Reviewed' }, { value: 'all', label: 'All review states' }]} />
            <FilterSelect label="BPM state" value={bpmState} onChange={setBpmState} options={[{ value: 'all', label: 'All BPM states' }, { value: 'suggested', label: 'Suggested only' }, { value: 'confirmed', label: 'Confirmed or trusted' }, { value: 'missing', label: 'No BPM data' }]} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="outline" className="rounded-full border-white/20">{data?.pagination?.total?.toLocaleString() || 0} match filters</Badge>
              <span className="text-muted-foreground">{selected.size.toLocaleString()} selected with revisions locked</span>
              <Button variant="link" size="sm" className="h-auto px-1 text-primary" onClick={selectAllMatching} disabled={working || !data?.pagination?.total}>Select all matching filters</Button>
              {selected.size > 0 && <Button variant="link" size="sm" className="h-auto px-1 text-muted-foreground" onClick={() => setSelected(new Map())}>Clear</Button>}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="rounded-full bg-primary text-black hover:bg-primary/90" disabled={!selected.size || working} onClick={() => runBulk({ path: '/api/admin/metadata/bulk/confirm-bpm', body: {}, successLabel: 'Suggested BPM confirmed' })}><Check className="w-4 h-4 mr-2" />Confirm suggested BPM</Button>
              <Button size="sm" variant="outline" className="rounded-full border-violet-500/50 text-violet-400" disabled={!selected.size || working} onClick={() => setKeyDialog(true)}><KeyRound className="w-4 h-4 mr-2" />Set key</Button>
              <Button size="sm" variant="outline" className="rounded-full border-accent/50 text-accent" disabled={!selected.size || working} onClick={() => setBulkEditDialog(true)}><Tags className="w-4 h-4 mr-2" />Edit titles and tags</Button>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-card/40 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1180px] text-sm">
              <thead className="bg-black/30 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="w-10 px-3 py-2 text-left"><Checkbox checked={allPageSelected} onCheckedChange={togglePage} aria-label="Select page" /></th>
                  <th className="w-48 px-2 py-2 text-left">Preview</th>
                  <th className="min-w-56 px-2 py-2 text-left">Title</th>
                  <th className="px-2 py-2 text-left">Instrument</th>
                  <th className="px-2 py-2 text-left">Tempo</th>
                  <th className="px-2 py-2 text-left">Key</th>
                  <th className="min-w-52 px-2 py-2 text-left">Tags</th>
                  <th className="px-2 py-2 text-left">Pack</th>
                  <th className="w-24 px-2 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {rows.map((row, index) => (
                  <tr
                    key={row.id}
                    ref={element => { rowRefs.current[index] = element; }}
                    tabIndex={0}
                    onKeyDown={event => onRowKeyDown(event, row, index)}
                    className={`focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/70 hover:bg-white/[0.035] ${selected.has(row.id) ? 'bg-primary/[0.06]' : ''}`}
                  >
                    <td className="px-3 py-2"><Checkbox checked={selected.has(row.id)} onCheckedChange={() => toggleRow(row)} aria-label={`Select ${row.title}`} /></td>
                    <td className="px-2 py-2">
                      {row.preview_url ? <audio data-metadata-preview controls preload="none" src={audioUrl(row.preview_url)} onPlay={onAudioPlay} className="h-8 w-44" aria-label={`Preview ${row.title}`} /> : <span className="text-xs text-muted-foreground">No preview</span>}
                    </td>
                    <td className="px-2 py-2"><div className="font-medium leading-tight">{row.title}</div><div className="text-[10px] text-muted-foreground mt-1">rev {row.revision}</div></td>
                    <td className="px-2 py-2"><Badge variant="outline" className="rounded-full border-white/15 font-normal">{row.instrument_group || 'Unknown'}</Badge></td>
                    <td className="px-2 py-2">
                      {row.bpm != null ? <div><span className="font-semibold text-emerald-400">{row.bpm} BPM</span><div className="text-[10px] text-muted-foreground">{row.bpm_source === 'confirmed' ? 'staff confirmed' : 'trusted catalog'}</div></div> : row.bpm_suggested != null ? <div className="rounded-md border border-dashed border-amber-400/50 bg-amber-400/10 px-2 py-1 inline-block"><div className="font-semibold text-amber-300">{row.bpm_suggested} suggested</div><div className="text-[10px] text-amber-200/70">from {row.bpm_suggested_bars || '?'} bars</div></div> : <span className="text-muted-foreground">Missing</span>}
                    </td>
                    <td className="px-2 py-2">{row.musical_key || <span className="text-muted-foreground">Not set</span>}</td>
                    <td className="px-2 py-2"><div className="flex flex-wrap gap-1">{row.tags.length ? row.tags.map(tag => <span key={tag.toLocaleLowerCase()} className="rounded bg-white/5 px-1.5 py-0.5 text-xs text-muted-foreground">{tag}</span>) : <span className="text-muted-foreground">None</span>}</div></td>
                    <td className="px-2 py-2 text-xs text-muted-foreground">{row.packs.length ? row.packs.map(item => item.name).join(', ') : 'Unassigned'}</td>
                    <td className="px-2 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {row.bpm == null && row.bpm_suggested != null && <Button size="icon" variant="ghost" className="w-8 h-8 rounded-full text-primary" title="Confirm this BPM" aria-label={`Confirm BPM for ${row.title}`} onClick={() => performAction({ path: `/api/admin/metadata/loops/${row.id}`, method: 'PATCH', body: { expected_revision: row.revision, confirm_suggested_bpm: true }, successLabel: 'Suggested BPM confirmed' })}><Check className="w-4 h-4" /></Button>}
                        <Button size="icon" variant="ghost" className="w-8 h-8 rounded-full" title="Edit loop" aria-label={`Edit ${row.title}`} onClick={() => openRowEdit(row)}><Edit3 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && <tr><td colSpan="9" className="px-4 py-16 text-center text-muted-foreground"><Headphones className="w-8 h-8 mx-auto mb-2 opacity-50" />No loops match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="border-t border-white/10 px-3 py-2 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Page {data?.pagination?.page || 1} of {data?.pagination?.pages || 1}</span>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="w-8 h-8 rounded-full" disabled={page <= 1 || loading} onClick={() => setPage(value => value - 1)} aria-label="Previous page"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" className="w-8 h-8 rounded-full" disabled={page >= (data?.pagination?.pages || 1) || loading} onClick={() => setPage(value => value + 1)} aria-label="Next page"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </section>
        <p className="text-xs text-muted-foreground">Keyboard: focus a row, use Up and Down to move, Space or Enter to select, and E to edit. Audio controls remain fully keyboard accessible.</p>
      </main>

      <Dialog open={keyDialog} onOpenChange={setKeyDialog}>
        <DialogContent className="bg-card border-white/10"><DialogHeader><DialogTitle>Set musical key for {selected.size} loops</DialogTitle></DialogHeader><div className="space-y-2"><label htmlFor="bulk-key" className="text-sm text-muted-foreground">Manual key</label><Input id="bulk-key" list="musical-keys" value={keyValue} onChange={event => setKeyValue(event.target.value)} placeholder="F# min" autoFocus /><datalist id="musical-keys">{MUSICAL_KEYS.map(key => <option key={key} value={key} />)}</datalist><p className="text-xs text-muted-foreground">Keys are staff-entered only. No inferred key is shown or accepted.</p></div><DialogFooter><Button variant="ghost" onClick={() => setKeyDialog(false)}>Cancel</Button><Button disabled={!keyValue.trim() || working} onClick={() => runBulk({ path: '/api/admin/metadata/bulk/set-key', body: { musical_key: keyValue }, successLabel: 'Musical key set' })}>Apply key</Button></DialogFooter></DialogContent>
      </Dialog>

      <Dialog open={bulkEditDialog} onOpenChange={setBulkEditDialog}>
        <DialogContent className="bg-card border-white/10 max-w-xl"><DialogHeader><DialogTitle>Edit titles and tags for {selected.size} loops</DialogTitle></DialogHeader><div className="space-y-5">
          <EditMode label="Titles" value={bulkEdit.titleMode} onChange={value => setBulkEdit(current => ({ ...current, titleMode: value }))} />
          {bulkEdit.titleMode === 'set' && <Input value={bulkEdit.title} onChange={event => setBulkEdit(current => ({ ...current, title: event.target.value }))} placeholder="Exact title for every selected loop" />}
          {bulkEdit.titleMode === 'replace' && <div className="grid grid-cols-2 gap-2"><Input value={bulkEdit.titleFind} onChange={event => setBulkEdit(current => ({ ...current, titleFind: event.target.value }))} placeholder="Find literal text" /><Input value={bulkEdit.titleReplace} onChange={event => setBulkEdit(current => ({ ...current, titleReplace: event.target.value }))} placeholder="Replace with, blank removes" /></div>}
          <EditMode label="Tags" value={bulkEdit.tagMode} onChange={value => setBulkEdit(current => ({ ...current, tagMode: value }))} />
          {bulkEdit.tagMode === 'set' && <Input value={bulkEdit.tags} onChange={event => setBulkEdit(current => ({ ...current, tags: event.target.value }))} placeholder="Comma-separated tags, blank clears" />}
          {bulkEdit.tagMode === 'replace' && <div className="grid grid-cols-2 gap-2"><Input value={bulkEdit.tagFind} onChange={event => setBulkEdit(current => ({ ...current, tagFind: event.target.value }))} placeholder="Find literal text" /><Input value={bulkEdit.tagReplace} onChange={event => setBulkEdit(current => ({ ...current, tagReplace: event.target.value }))} placeholder="Replace with, blank removes" /></div>}
          <div className="rounded-lg bg-amber-400/10 border border-amber-400/20 px-3 py-2 text-xs text-amber-200">Find and replace is literal and case-sensitive. It never runs as a regular expression. Empty tags and case-insensitive duplicates are removed.</div>
        </div><DialogFooter><Button variant="ghost" onClick={() => setBulkEditDialog(false)}>Cancel</Button><Button disabled={working} onClick={submitBulkEdit}>Apply edits</Button></DialogFooter></DialogContent>
      </Dialog>

      <Dialog open={Boolean(editingLoop)} onOpenChange={open => !open && setEditingLoop(null)}>
        <DialogContent className="bg-card border-white/10 max-w-lg"><DialogHeader><DialogTitle>Edit one loop</DialogTitle></DialogHeader>{editingLoop && <div className="space-y-4">
          <div><label htmlFor="loop-title" className="text-sm text-muted-foreground">Title</label><Input id="loop-title" value={editForm.title} onChange={event => setEditForm(current => ({ ...current, title: event.target.value }))} /></div>
          <div><label htmlFor="loop-tags" className="text-sm text-muted-foreground">Tags</label><Input id="loop-tags" value={editForm.tags} onChange={event => setEditForm(current => ({ ...current, tags: event.target.value }))} placeholder="Comma-separated tags" /></div>
          <div><label htmlFor="loop-key" className="text-sm text-muted-foreground">Musical key</label><Input id="loop-key" list="musical-keys" value={editForm.musicalKey} onChange={event => setEditForm(current => ({ ...current, musicalKey: event.target.value }))} placeholder="Not set" /></div>
          {editingLoop.bpm == null && editingLoop.bpm_suggested != null && <label className="flex items-center gap-3 rounded-lg border border-amber-400/30 bg-amber-400/10 p-3"><Checkbox checked={editForm.confirmBpm} onCheckedChange={checked => setEditForm(current => ({ ...current, confirmBpm: Boolean(checked) }))} /><span className="text-sm">Confirm {editingLoop.bpm_suggested} BPM from {editingLoop.bpm_suggested_bars || '?'} bars</span></label>}
        </div>}<DialogFooter><Button variant="ghost" onClick={() => setEditingLoop(null)}>Cancel</Button><Button disabled={!editForm.title.trim() || working} onClick={submitRowEdit}>Save and make undoable</Button></DialogFooter></DialogContent>
      </Dialog>

      <Dialog open={Boolean(pendingAction)} onOpenChange={open => !open && setPendingAction(null)}>
        <DialogContent className="bg-card border-amber-400/30 max-w-md"><DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-400" />Confirm large bulk action</DialogTitle></DialogHeader><p className="text-sm text-muted-foreground">This will update {selectionRows.length} loops in one transaction. The selection is revision-locked and the action can be undone as one batch.</p><DialogFooter><Button variant="ghost" onClick={() => setPendingAction(null)}>Cancel</Button><Button onClick={() => performAction(pendingAction)}>Confirm {selectionRows.length} updates</Button></DialogFooter></DialogContent>
      </Dialog>

      {working && <div className="fixed bottom-5 right-5 z-50 rounded-full bg-black/85 border border-white/10 px-4 py-2 flex items-center gap-2 text-sm shadow-xl"><Loader2 className="w-4 h-4 animate-spin text-primary" />Applying safely</div>}
    </div>
  );
};


const Stat = ({ label, value, tone }) => <div className="rounded-2xl border border-white/10 bg-card/60 p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p><p className={`text-2xl font-bold mt-1 ${tone}`}>{Number(value || 0).toLocaleString()}</p></div>;

const FilterSelect = ({ label, value, onChange, options }) => <div><label className="text-[11px] uppercase tracking-wider text-muted-foreground ml-1">{label}</label><Select value={value} onValueChange={onChange}><SelectTrigger className="h-9 mt-1 bg-black/20 border-white/10"><SelectValue /></SelectTrigger><SelectContent>{options.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select></div>;

const EditMode = ({ label, value, onChange }) => <div className="flex items-center justify-between gap-3"><span className="font-medium">{label}</span><Select value={value} onValueChange={onChange}><SelectTrigger className="w-48 h-9 bg-black/20 border-white/10"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Leave unchanged</SelectItem><SelectItem value="set">Set exact value</SelectItem><SelectItem value="replace">Find and replace</SelectItem></SelectContent></Select></div>;

export default AdminMetadataReview;
